/* External */
import { PureComponent } from 'react';
import PropTypes from 'prop-types';

/* Components */
import {
  VodFocusableProgressBar, LiveFocusableProgressBar,
  KeyCodeToScrubbingDirection, Key, ScrubbingDirection, ScrubbingSpeed,
} from '../FocusableProgressBar';
import PlayPause from '../PlayPause';
import LiveRemainingTime from './LiveRemainingTime';
import VodRemainingTime from './VodRemainingTime';
import LiveCurrentTime from './LiveCurrentTime';
import VodCurrentTime from './VodCurrentTime';
import ConditionalVisible from '../ConditionalVisible';

/* Utils */
import withCursorVisibilityFilterFocusable from '../../../../hocs/withCursorVisibilityFilter.hoc';
import { isLeftButtonMouseEvent } from '../../../../utils/functions';
import { KeyCodesToKeyName } from '../../../../utils/keyDownEvents';
import platform from '../../../../utils/platform';
import { ProgressBarContainerClassNames } from './utils';
// Commented out to disable GTM reporting because it was causing performance issues
// import { GTM_EVENT_NAMES } from '../../../../utils/googleTags';

/* Styles */
import './ProgressBarContainer.scss';

const SEEKABLE_START_MESSAGE_TIME = 4000;
const LIKE_ENTER_WHEN_SCRUBBING_KEYS = new Set([
  platform.keyCodes.mediaControl.play,
  platform.keyCodes.mediaControl.pause,
  platform.keyCodes.mediaControl.playPause,
  platform.keyCodes.navigation.enter,
]);
const KeyCodeToGTMEventName = {
  // Commented out to disable GTM reporting because it was causing performance issues
  // [platform.keyCodes.navigation.left]: GTM_EVENT_NAMES.PLAYER.BACKWARD,
  // [platform.keyCodes.navigation.right]: GTM_EVENT_NAMES.PLAYER.FORWARD,
};

class ProgressBarContainer extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      displaySeekableStartMessage: false,
      progressBarFocused: false,
    };
    this.handlePlayPauseMouseDown = this.handlePlayPauseMouseDown.bind(this);
    this.handleProgressBarKeyDown = this.handleProgressBarKeyDown.bind(this);
    this.handleSeekingStart = this.handleSeekingStart.bind(this);
    this.handleSeekingEnd = this.handleSeekingEnd.bind(this);
    this.handleSeekableStartReached = this.handleSeekableStartReached.bind(this);
    this.handleBecameFocused = this.handleBecameFocused.bind(this);
    this.handleBecameBlurred = this.handleBecameBlurred.bind(this);
  }

  handlePlayPauseMouseDown(event) {
    const { togglePlayPause } = this.props;
    if (isLeftButtonMouseEvent(event)) {
      togglePlayPause(event);
    }
  }

  handleProgressBarKeyDown(event) {
    const {
      togglePlayPause,
      playerCommonTagEvent,
      scrubbing,
      onNextScrubbing,
      onClearScrubbing,
      isDelayed,
      isLive,
    } = this.props;

    const scrubbingDirection = KeyCodeToScrubbingDirection[event.keyCode];
    if (scrubbing && scrubbingDirection) {
      onNextScrubbing(KeyCodeToScrubbingDirection[event.keyCode]);
      return false;
    }

    if (this.seeking && LIKE_ENTER_WHEN_SCRUBBING_KEYS.has(event.keyCode)) {
      onClearScrubbing();
      return KeyCodesToKeyName[platform.keyCodes.navigation.enter];
    }

    if (!this.seeking && event.keyCode === platform.keyCodes.navigation.enter) {
      togglePlayPause();
      return false;
    }

    if (event.keyCode === platform.keyCodes.back) {
      if (this.seeking) {
        onClearScrubbing();
        return Key.ESCAPE;
      }
      return false;
    }

    if (
      isLive
        && !isDelayed
        && KeyCodeToScrubbingDirection[event.keyCode] === ScrubbingDirection.FORWARD
    ) {
      return false;
    }

    const eventName = KeyCodeToGTMEventName[event.keyCode];
    if (eventName && playerCommonTagEvent) playerCommonTagEvent({ eventName });

    return KeyCodesToKeyName[event.keyCode] || false;
  }

  handleSeekingStart() {
    const { setFocus, onSeekingStart } = this.props;
    setFocus();
    this.seeking = true;
    if (onSeekingStart) onSeekingStart();
  }

  handleSeekingEnd() {
    const { onSeekingEnd } = this.props;
    this.seeking = false;
    if (onSeekingEnd) onSeekingEnd();
  }

  handleSeekableStartReached() {
    const { onSeekableStartReached } = this.props;
    this.setState({ displaySeekableStartMessage: true });
    setTimeout(() => {
      this.setState({ displaySeekableStartMessage: false });
    }, SEEKABLE_START_MESSAGE_TIME);
    if (onSeekableStartReached) onSeekableStartReached();
  }

  handleBecameFocused() {
    this.setState({ progressBarFocused: true });
  }

  handleBecameBlurred() {
    this.setState({ progressBarFocused: false });
  }

  render() {
    const {
      scrubbing,
      onMouseEnter,
      onMouseLeave,
      isScrubInProgress,
      isLive,
      hasFocusedChild,
      focused,
      className,
      onArrowPress,
      onSeekableEndReached,
    } = this.props;

    const { displaySeekableStartMessage, progressBarFocused } = this.state;

    const ProgressBar = isLive ? LiveFocusableProgressBar : VodFocusableProgressBar;
    const RemainingTime = isLive ? LiveRemainingTime : VodRemainingTime;
    const CurrentTime = isLive ? LiveCurrentTime : VodCurrentTime;

    return (
      <div
        className={ProgressBarContainerClassNames.block(className)}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <ConditionalVisible visible={!isScrubInProgress}>
          <PlayPause
            onMouseDown={this.handlePlayPauseMouseDown}
            className={ProgressBarContainerClassNames.element('play-pause')}
            focused={hasFocusedChild || focused || progressBarFocused}
          />
        </ConditionalVisible>
        <ProgressBar
          className={ProgressBarContainerClassNames.element('progress-bar')}
          displaySeekableStartMessage={displaySeekableStartMessage}
          isScrubbing={isScrubInProgress}
          scrubbing={scrubbing}
          onSeekingStart={this.handleSeekingStart}
          onSeekingEnd={this.handleSeekingEnd}
          onSeekableStartReached={this.handleSeekableStartReached}
          onSeekableEndReached={onSeekableEndReached}
          onKeyDown={this.handleProgressBarKeyDown}
          onArrowPress={onArrowPress}
          onBecameFocused={this.handleBecameFocused}
          onBecameBlurred={this.handleBecameBlurred}
        />
        <div className={ProgressBarContainerClassNames.element('times')}>
          <CurrentTime />
          <RemainingTime />
        </div>
      </div>
    );
  }
}

ProgressBarContainer.propTypes = {
  className: PropTypes.string,
  focused: PropTypes.bool.isRequired,
  hasFocusedChild: PropTypes.bool.isRequired,
  setFocus: PropTypes.func.isRequired,
  isLive: PropTypes.bool,
  isScrubInProgress: PropTypes.bool.isRequired,
  isDelayed: PropTypes.bool,
  scrubbing: PropTypes.shape({
    direction: PropTypes.oneOf(Object.values(ScrubbingDirection)),
    speed: PropTypes.oneOf(Object.values(ScrubbingSpeed)),
  }),
  togglePlayPause: PropTypes.func.isRequired,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onSeekingStart: PropTypes.func,
  onSeekingEnd: PropTypes.func,
  onSeekableStartReached: PropTypes.func,
  onSeekableEndReached: PropTypes.func,
  onArrowPress: PropTypes.func,
  onNextScrubbing: PropTypes.func.isRequired,
  onClearScrubbing: PropTypes.func.isRequired,
  playerCommonTagEvent: PropTypes.func,
};

ProgressBarContainer.defaultProps = {
  className: '',
  isLive: false,
  isDelayed: false,
  scrubbing: undefined,
  onMouseEnter: undefined,
  onMouseLeave: undefined,
  onSeekingStart: undefined,
  onSeekingEnd: undefined,
  onSeekableStartReached: undefined,
  onSeekableEndReached: undefined,
  onArrowPress: undefined,
  playerCommonTagEvent: undefined,
};

export default withCursorVisibilityFilterFocusable({ trackChildren: true })(ProgressBarContainer);
